import React, { ReactElement } from 'react';
import styled from 'styled-components';
import audioIcon from '../images/audio.svg';
import textIcon from '../images/text.svg';
import videoIcon from '../images/video.svg';
import launchIcon from '../images/launch.svg';
import itemAudioDefault from '../images/item-audio-default.png';
import itemTextDefault from '../images/item-text-default.png';
import itemVideoDefault from '../images/item-video-default.png';
import { BlogPostItem, Item } from '../types';
import { StyledLink } from './common';
import { useBreadcrumbsString } from '../hooks';
import { fromNow } from '../datetimeFormat';
import { useTranslations } from 'next-intl';
import { getDurationMinutes } from '../utils';

const icons = {
  audio: audioIcon.src,
  text: textIcon.src,
  video: videoIcon.src,
  ['blog-post']: textIcon.src,
};

const defaultImageSource = {
  audio: itemAudioDefault.src,
  text: itemTextDefault.src,
  video: itemVideoDefault.src,
  ['blog-post']: itemTextDefault.src,
};

const RightIcon = styled.img`
  width: 1em;
`;

const ItemTypeIcon = styled.div<{ $icon: string }>`
  vertical-align: middle;
  margin-left: 0.5em;
  margin-right: 0.5em;
  width: 16px;
  height: 16px;
  background-image: url(${(props) => props.$icon || ''});
  background-size: contain;
  background-repeat: no-repeat;
  display: inline-block;
`;

const ListItem = styled.li`
  background-color: #fff;
  border-radius: 54px;
  padding: 0.5em;
  margin-bottom: 0.5em;
  text-align: left;

  @media (max-width: 350px) {
    border-radius: 0;
    margin-left: -16px;
    margin-right: -16px;
  }
`;

const ItemPreview = styled(ListItem)`
  padding: 0.5em 2em;
  display: flex;
  align-items: center;

  @media (max-width: 350px) {
    padding: 0.5em 1em;
  }

  & img {
    vertical-align: middle;
    margin: auto 1em auto 0;
  }

  @media (max-width: 450px) {
    & img {
      border-radius: 0;
      width: 32px;
      height: 32px;
    }
  }

  &.private {
    opacity: 0.5;
    border: 1px dashed darkred;
  }
`;

const ItemTitle = styled.div`
  font-weight: bold;
`;
const ItemBreadcrumbs = styled.div`
  font-size: 0.8em;
  font-style: italic;
`;

const ItemAuthor = styled.div`
  font-style: italic;
  font-size: 0.9em;
`;
const ItemDuration = styled.span`
  font-size: 0.8em;
`;
const ItemDetails = styled.div`
  margin-top: 0.5em;
`;
const MetaSeparator = styled.span`
  color: #999;
  font-size: 0.8em;
  margin-left: 0.4em;
  margin-right: 0.4em;
`;
const MetaText = styled.span`
  font-size: 0.8em;
`;

const separator = '•';

function isBlogPostItem(item: Item | BlogPostItem): item is BlogPostItem {
  return item.type === 'blog-post';
}

const Author = ({
  item,
}: {
  item: Item | BlogPostItem;
}): ReactElement | null => {
  const t = useTranslations();
  const isBlogPost = isBlogPostItem(item);
  if (isBlogPost) {
    const url = new URL(item.link);
    return (
      <ItemAuthor>
        {t('itemView.blogPostFrom', { blogUrl: url.hostname })}
      </ItemAuthor>
    );
  } else if (item.author) {
    return (
      <ItemAuthor>{t('itemView.byline', { author: item.author })}</ItemAuthor>
    );
  }
  return null;
};

const ItemInList = ({
  item,
  showBreadcrumbs,
}: {
  item: Item | BlogPostItem;
  showBreadcrumbs: boolean;
}) => {
  const t = useTranslations();
  const breadcrumbs = useBreadcrumbsString({ item, showBreadcrumbs });
  const isBlogPost = isBlogPostItem(item);
  const blogPostProps = isBlogPost
    ? {
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {};
  const link = isBlogPost ? item.link : `/item/${item.slug}`;
  const thumbnail = `https://pvappitemimages.b-cdn.net/${item.id}/thumb@1x.jpg`;
  const availableSizesThumbnails = ['1.5x', '2x', '2.5x', '3x', '3.5x'].map(
    (size) => {
      return `https://pvappitemimages.b-cdn.net/${item.id}/thumb@${size}.jpg ${size}`;
    },
  );
  const srcSetThumbs = availableSizesThumbnails.join(',');

  return (
    <StyledLink href={link} key={item.id} {...blogPostProps}>
      <ItemPreview
        className={!isBlogPost && item?.visibility ? item.visibility : 'public'}
      >
        <img
          src={thumbnail}
          srcSet={srcSetThumbs}
          onError={(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
            // Handle 404s and potential infinite loops
            event.currentTarget.srcset = '';
            event.currentTarget.src = defaultImageSource[item.type];
          }}
          width={54}
          height={54}
          alt=""
        />
        <div>
          {showBreadcrumbs && <ItemBreadcrumbs>{breadcrumbs}</ItemBreadcrumbs>}
          <ItemTitle>{item.title}</ItemTitle>
          <Author item={item} />
          <ItemDetails>
            <ItemTypeIcon
              $icon={icons[item.type]}
              // See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/img_role#using_roleimg_to_confer_meaning_that_is_obscured_or_implied
              role="img"
              aria-label={item.type}
            />
            <ItemDuration>{getDurationMinutes(item.duration)}</ItemDuration>
            {item.createdAt && (
              <>
                <MetaSeparator>{separator}</MetaSeparator>
                <MetaText>{fromNow(item.createdAt.seconds)}</MetaText>
              </>
            )}
          </ItemDetails>
        </div>
        {isBlogPost && (
          <RightIcon
            src={launchIcon.src}
            alt={t('itemView.openInNewTab')}
            width={16}
            height={16}
          />
        )}
      </ItemPreview>
    </StyledLink>
  );
};

export default ItemInList;
